import React, { useState, useEffect } from 'react';
import { Card, CardBody, Spinner } from '@heroui/react';
import {
  Plus,
  MessageSquare,
  Tag,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import {
  getTicketsByEmail,
  SupportTicket,
} from '../../../../service/supportTicketService';
import { useAuth } from '../../../../context/AuthContext';
import CustomButton from '../../../components/CustomButton';
import PageHeader from '../../../components/common/ui/PageHeader';

const SupportTicketPage: React.FC = () => {
  const { user } = useAuth();
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  const fetchTickets = async () => {
    if (!user?.email) {
      setError('User email not found');
      setLoading(false);
      return;
    }

    try {
      const response = await getTicketsByEmail(user.email, 'customer');
      if (response.success) {
        setTickets(response.tickets || []);
        setError('');
      } else {
        setError('Failed to fetch tickets');
      }
    } catch (err: any) {
      console.error('Error fetching tickets:', err);
      setError(err.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [user?.email]);

  const handleCreateNewTicket = () => {
    // Open support ticket form in a new tab
    window.open('/support-ticket', '_blank');
  };

  const getCategoryLabel = (category: string) => {
    const categoryMap: { [key: string]: string } = {
      technical: 'Technical Issue',
      billing: 'Billing Question',
      feature: 'Feature Request',
      general: 'General Question',
    };
    return categoryMap[category] || category;
  };

  const getPriorityColor = (priority: string) => {
    const colorMap: { [key: string]: string } = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800',
    };
    return colorMap[priority] || 'bg-gray-100 text-gray-800';
  };

  const getStatusColor = (status: string) => {
    const colorMap: { [key: string]: string } = {
      open: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
    };
    return colorMap[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertCircle className="h-3 w-3" />;
      case 'in_progress':
        return <Clock className="h-3 w-3" />;
      case 'resolved':
        return <CheckCircle className="h-3 w-3" />;
      case 'closed':
        return <XCircle className="h-3 w-3" />;
      default:
        return <AlertCircle className="h-3 w-3" />;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Support Tickets"
          description="Manage and track your support requests"
        />
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <PageHeader
        title="Support Tickets"
        description="Manage and track your support requests"
      />

      {/* Action below the Support Tickets heading */}
      <div className="mt-2 mb-6">
        <CustomButton
          onClick={handleCreateNewTicket}
          className="bg-primary text-white hover:bg-primary-hover"
          startContent={<Plus className="h-4 w-4" />}
        >
          Create New Ticket
        </CustomButton>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <XCircle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Tickets List */}
      {tickets.length === 0 ? (
        <Card className="shadow-lg">
          <CardBody className="p-8 text-center">
            <MessageSquare className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Support Tickets Yet
            </h3>
            <p className="text-gray-600 mb-6">
              You haven't submitted any support tickets yet. Click the button
              below to create your first ticket.
            </p>
            <CustomButton
              onClick={handleCreateNewTicket}
              className="bg-primary text-white hover:bg-primary-hover"
              startContent={<Plus className="h-4 w-4" />}
            >
              Create Your First Ticket
            </CustomButton>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {tickets.map((ticket) => (
            <Card key={ticket.id} className="hover:shadow-lg transition-shadow">
              <CardBody className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {ticket.subject}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3">
                      {ticket.description}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                        ticket.priority
                      )}`}
                    >
                      {ticket.priority}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 ${getStatusColor(
                        ticket.status
                      )}`}
                    >
                      {getStatusIcon(ticket.status)}
                      {ticket.status}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Tag className="h-4 w-4 mr-1" />
                      <span>{getCategoryLabel(ticket.category)}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>Created: {formatDate(ticket.createdTime)}</span>
                    </div>
                  </div>
                  <div className="text-xs text-gray-400">
                    Ticket #{ticket.id}
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportTicketPage;
